import { useEffect, useRef } from 'react';
import { Formio } from '@formio/js';
import type { FormData, FormioSchema } from './api';

interface Props {
  schema: FormioSchema;
  initialData: FormData;
  onChange: (data: FormData) => void;
  readOnly?: boolean;
  fileUploadUrl?: string;
}

// Point any formio "file" components at our upload endpoint (url storage). Walks the
// whole schema tree so nested (panel/columns/container) file fields are covered too.
function configureFileComponents(node: unknown, url: string): void {
  if (Array.isArray(node)) {
    for (const item of node) configureFileComponents(item, url);
    return;
  }
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    if (obj.type === 'file') {
      obj.storage = 'url';
      obj.url = url;
    }
    for (const value of Object.values(obj)) configureFileComponents(value, url);
  }
}

// Remove the builder's default "Submit" button(s) from the schema — the renderer renders
// its own Submit control (which drives the API call, draft token and submit states), so a
// formio submit button would just be a confusing duplicate.
function stripSubmitButtons(node: unknown): void {
  if (Array.isArray(node)) {
    for (const item of node) stripSubmitButtons(item);
    return;
  }
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    if (Array.isArray(obj.components)) {
      obj.components = (obj.components as unknown[]).filter((c) => {
        const comp = c as Record<string, unknown>;
        // A formio button submits when its action is "submit" OR when no action is
        // set (submit is the default), so strip both — the renderer draws its own
        // Submit. Buttons with an explicit non-submit action (reset/url/event/…) stay.
        const action = comp.action;
        const submits = action === undefined || action === null || action === '' || action === 'submit';
        return !(comp.type === 'button' && submits);
      });
    }
    for (const value of Object.values(obj)) stripSubmitButtons(value);
  }
}

// The @formio/js form instance is loosely typed for interop.
type FormInstance = {
  submission: { data: FormData };
  on: (event: string, handler: () => void) => void;
  destroy: () => void;
};

/** Renders a formio.js form and reports data changes. Remount (via `key`) to load a new form. */
export function FormioForm({ schema, initialData, onChange, readOnly = false, fileUploadUrl }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const schemaRef = useRef(
    (() => {
      const cloned = JSON.parse(JSON.stringify(schema)) as FormioSchema;
      if (fileUploadUrl) {
        configureFileComponents(cloned, fileUploadUrl);
      }
      stripSubmitButtons(cloned);
      return cloned;
    })(),
  );
  const initialRef = useRef(initialData);
  const readOnlyRef = useRef(readOnly);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    let instance: FormInstance | undefined;
    let cancelled = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Formio as any)
      .createForm(host, schemaRef.current, { noAlerts: true, readOnly: readOnlyRef.current })
      .then((form: FormInstance) => {
        if (cancelled) {
          form.destroy();
          return;
        }
        instance = form;
        form.submission = { data: initialRef.current };
        form.on('change', () => onChangeRef.current(form.submission.data));
      });

    return () => {
      cancelled = true;
      instance?.destroy();
    };
  }, []);

  return <div ref={hostRef} />;
}
